import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Clock, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";
import UsageTour from "../../components/UsageTour";
import BackButton from "../../components/BackButton";
import { Spinner, formatRupiah } from "../../components/ui";

const STATUS_FILTERS = ["Semua", "Menunggu", "Diterima", "Berlangsung", "Selesai"];

const STATUS_MAP = {
  PENDING_WORKER: { label: "Menunggu", color: "bg-amber-50 text-amber-600", icon: Clock },
  ACCEPTED: { label: "Diterima", color: "bg-blue-50 text-blue-600", icon: CheckCircle2 },
  ON_PROGRESS: { label: "Berlangsung", color: "bg-brand-50 text-brand-600", icon: Clock },
  COMPLETED: { label: "Selesai", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  CONFIRMED: { label: "Dikonfirmasi", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak", color: "bg-red-50 text-red-600", icon: XCircle },
  CANCELLED: { label: "Dibatalkan", color: "bg-gray-50 text-gray-600", icon: XCircle },
};

const FILTER_STATUS = {
  Semua: null,
  Menunggu: ["PENDING_WORKER"],
  Diterima: ["ACCEPTED"],
  Berlangsung: ["ON_PROGRESS"],
  Selesai: ["COMPLETED", "CONFIRMED"],
};

export default function WorkerBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState(() => cacheGet(cacheKey(user?.id, "worker-bookings")).data || []);
  const [loading, setLoading] = useState(() => bookings.length === 0);
  const [filter, setFilter] = useState("Semua");

  useEffect(() => {
    const key = cacheKey(user?.id, "worker-bookings");
    const fetchBookings = async () => {
      try {
        const { data } = await api.get("/api/bookings");
        setBookings(data);
        cacheSet(key, data);
      } catch (err) {
        console.error("Failed to load bookings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [user?.id]);

  if (loading) return <Spinner />;

  const filtered = bookings.filter((b) => {
    const allowed = FILTER_STATUS[filter];
    return !allowed || allowed.includes(b.status);
  });

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      {!loading && (
        <UsageTour
          tourKey="tour-worker-bookings-v1"
          continueKey="tour-worker-bookings"
          auto={false}
          steps={
            bookings.length > 0
              ? [
                  {
                    selector: `booking-card-${bookings[0].id}`,
                    title: "Kartu pesanan 👆",
                    text: "Buka kartunya untuk TERIMA/TOLAK, lalu MULAI (foto sebelum wajib) dan SELESAI (foto sesudah + catatan wajib). Jangan lupa: tanpa foto, tombol tidak jalan!",
                  },
                ]
              : [
                  {
                    title: "Belum ada pesanan 📭",
                    text: "Wajar — pelanggan memesan setelah kamu punya jasa + foto + status Terverifikasi. Cek Jasa Saya dan Profil dulu, pesanan akan muncul di sini.",
                    to: "/worker/services",
                    toLabel: "Lengkapi Jasaku",
                  },
                ]
          }
        />
      )}
      <div className="container space-y-6">
        <BackButton to="/worker" />
        <div>
          <h1 className="text-brand-800">Pesanan Masuk</h1>
          <p className="text-brand-400 mt-1">Kelola pesanan dari pelanggan</p>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`chip whitespace-nowrap transition ${
                filter === f
                  ? "bg-brand-600 text-white"
                  : "bg-brand-50 text-brand-600 hover:bg-brand-100"
              }`}
              data-testid={`filter-${f.toLowerCase()}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Booking list */}
        {filtered.length === 0 ? (
          <div className="card p-10 text-center">
            <ClipboardList className="h-12 w-12 text-brand-200 mx-auto" />
            <p className="font-bold text-brand-800 mt-4">Belum ada pesanan</p>
            <p className="text-brand-400 mt-1">Pesanan dari pelanggan akan muncul di sini.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => (
              <WorkerBookingCard key={b.id} booking={b} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WorkerBookingCard({ booking }) {
  const status = STATUS_MAP[booking.status] || STATUS_MAP.PENDING_WORKER;
  const StatusIcon = status.icon;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <Link
      to={`/worker/bookings/${booking.id}`}
      className="card p-4 hover:border-brand-200 transition block"
      data-testid={`booking-card-${booking.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-bold text-brand-800">{booking.service?.name || "Layanan"}</p>
          <p className="text-sm text-brand-500 mt-1">dari {booking.customer?.display_name || "Pelanggan"}</p>
          <p className="text-sm text-brand-400 mt-0.5">{formatDate(booking.scheduled_at || booking.created_at)}</p>
        </div>
        <span className={`chip ${status.color}`}>
          <StatusIcon className="h-4 w-4" /> {status.label}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-brand-50 pt-3">
        <span className="font-extrabold text-brand-800">{formatRupiah(booking.quoted_amount)}</span>
        <div className="flex gap-2">
          {booking.status === "PENDING_WORKER" && (
            <span className="chip bg-amber-50 text-amber-600 text-xs">Perlu Ditanggapi</span>
          )}
          <span className="btn-ghost !py-2 !px-4 text-sm">
            Lihat Detail
          </span>
        </div>
      </div>
    </Link>
  );
}
